// src/modules/UserMultiSelectResponse.jsx
import React, { useState, useEffect } from "react";
import {
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormHelperText,
} from "@mui/material";
import axios from "axios";

const UserMultiSelectResponse = ({
  module,
  value, // previously saved selected user IDs (from responseData[module.id])
  isEditable, // controls whether the field is editable
  onChange, // callback to update the value
  companyId, // confirmed company ID (if available)
  requestedTo, // fallback value from responseData.requested_to
}) => {
  const [availableUsers, setAvailableUsers] = useState([]);
  const API_BASE_URL = "https://formbuilderapi.getforsite.com";

  // Use activeCompanyId: if companyId is falsy, fallback to requestedTo
  const activeCompanyId = companyId || requestedTo;

  useEffect(() => {
    const fetchUsers = async () => {
      if (activeCompanyId) {
        try {
          const response = await axios.get(`${API_BASE_URL}/api/users`, {
            params: { company_id: activeCompanyId },
          });
          setAvailableUsers(response.data.users);
        } catch (error) {
          console.error("Error fetching users:", error);
        }
      }
    };
    fetchUsers();
  }, [activeCompanyId, API_BASE_URL]);

  // Ensure the value is an array of selected user IDs
  const selectedUserIds = Array.isArray(value) ? value : [];

  const handleSelectionChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <Paper elevation={3} sx={{ padding: 2, marginY: 2 }}>
      <Typography variant="subtitle1">
        {module.label || "Select Users"}
      </Typography>
      <FormControl
        fullWidth
        variant="outlined"
        error={
          module.required && (!selectedUserIds || selectedUserIds.length === 0)
        }
      >
        <InputLabel>{module.label || "Select Users"}</InputLabel>
        <Select
          label={module.label || "Select Users"}
          multiple
          value={selectedUserIds}
          onChange={handleSelectionChange}
          disabled={!isEditable}
          renderValue={(selected) =>
            selected
              .map((userId) => {
                const user = availableUsers.find((u) => u.id === userId);
                return user ? `${user.first_name} ${user.last_name}` : userId;
              })
              .join(", ")
          }
        >
          {availableUsers.length > 0 ? (
            availableUsers.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                <Checkbox checked={selectedUserIds.indexOf(user.id) > -1} />
                <ListItemText
                  primary={`${user.first_name} ${user.last_name}`}
                />
              </MenuItem>
            ))
          ) : (
            <MenuItem value="" disabled>
              {activeCompanyId ? "Loading users..." : "Company ID not set"}
            </MenuItem>
          )}
        </Select>
        {module.required &&
          (!selectedUserIds || selectedUserIds.length === 0) && (
            <FormHelperText>Please select at least one user</FormHelperText>
          )}
      </FormControl>
    </Paper>
  );
};

export default UserMultiSelectResponse;
